import { forwardRef, useEffect, useRef, type TextareaHTMLAttributes } from 'react';

import { cn } from '@/utils/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  /** Grows with its content instead of scrolling inside a fixed box. */
  autoGrow?: boolean;
  /** Shows "used / limit" beneath the field. Needs `maxLength` to be meaningful. */
  showCount?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, autoGrow, showCount, value, maxLength, rows = 3, ...props }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
      if (!autoGrow) return;
      const node = internalRef.current;
      if (!node) return;
      node.style.height = 'auto';
      node.style.height = `${node.scrollHeight}px`;
    }, [autoGrow, value]);

    const length = typeof value === 'string' ? value.length : 0;

    return (
      <div className="w-full">
        <textarea
          ref={(node) => {
            internalRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          rows={rows}
          value={value}
          maxLength={maxLength}
          aria-invalid={invalid || undefined}
          className={cn(
            'w-full rounded-md border bg-surface px-3 py-2 text-body text-fg transition-colors',
            'placeholder:text-fg-tertiary focus:border-brand focus:outline-none',
            'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-disabled',
            autoGrow ? 'resize-none overflow-hidden' : 'resize-y',
            invalid ? 'border-danger' : 'border-border hover:border-border-strong',
            className,
          )}
          {...props}
        />

        {showCount && maxLength ? (
          <p
            className={cn(
              'mt-1 text-right text-caption tabular-nums',
              length >= maxLength ? 'text-danger' : 'text-fg-tertiary',
            )}
          >
            {length}/{maxLength}
          </p>
        ) : null}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
